import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { PairId, ProfileId } from "@/lib/types";

export type { PairId, ProfileId } from "@/lib/types";

export interface Profile {
  id: ProfileId;
  name: string;
  color: string;
  accent: string;
}

export const PROFILES: Record<ProfileId, Profile> = {
  matt: {
    id: "matt",
    name: "Matt",
    color: "#FF5E5B",
    accent: "bg-matt",
  },
  malcolm: {
    id: "malcolm",
    name: "Malcolm",
    color: "#3B9DFF",
    accent: "bg-malcolm",
  },
  pavel: {
    id: "pavel",
    name: "Pavel",
    color: "#7B61FF",
    accent: "bg-pavel",
  },
  batu: {
    id: "batu",
    name: "Batu",
    color: "#26C3A5",
    accent: "bg-batu",
  },
};

export const PROFILE_ORDER: readonly ProfileId[] = ["matt", "malcolm", "pavel", "batu"];

export const DUO_PAIRS: Record<PairId, readonly [ProfileId, ProfileId]> = {
  "matt-malcolm": ["matt", "malcolm"],
  "pavel-batu": ["pavel", "batu"],
};

export function pairForProfile(profile: ProfileId): PairId {
  return profile === "matt" || profile === "malcolm"
    ? "matt-malcolm"
    : "pavel-batu";
}

export function profilesForProfile(profile: ProfileId): readonly [ProfileId, ProfileId] {
  return DUO_PAIRS[pairForProfile(profile)];
}

export function opponentForProfile(profile: ProfileId): ProfileId {
  const pair = profilesForProfile(profile);
  return pair[0] === profile ? pair[1] : pair[0];
}

interface ProfileState {
  activeId: ProfileId;
  soundMuted: boolean;
  hapticsEnabled: boolean;
  setActive: (id: ProfileId) => void;
  toggleSound: () => void;
  toggleHaptics: () => void;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set) => ({
      activeId: "matt",
      soundMuted: false,
      hapticsEnabled: true,
      setActive: (id) => set({ activeId: id }),
      toggleSound: () => set((s) => ({ soundMuted: !s.soundMuted })),
      toggleHaptics: () => set((s) => ({ hapticsEnabled: !s.hapticsEnabled })),
    }),
    {
      name: "duomatt.profile",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const useActiveProfile = (): Profile =>
  PROFILES[useProfileStore((s) => s.activeId)];
